import { Button } from "components/ui/button";
import { useSelector } from "react-redux";
import { getUser } from "selectors/commonSelectors";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import { useDueInvoicesState } from "./use-due-invoices-state";
import { formatCurrencyFromCents } from "utils/currency-formatter";
import { loadChargebeeScript } from "utils/chargebee";
import { resolveChargebeeSite } from "utils/chargebee-site";

export function DueInvoicesBanner() {
  const user = useSelector(getUser);
  const queryClient = useQueryClient();
  const {
    shouldShowBanner,
    isDismissed,
    dismissBanner,
    dueInvoicesCount,
    totalDues,
    dueSince,
    currencyCode,
    isAdmin,
    adminEmail,
  } = useDueInvoicesState(user);

  if (!shouldShowBanner || isDismissed) {
    return null;
  }

  const handleUpdatePayment = async (e: React.MouseEvent) => {
    e.preventDefault();
    let chargebeeLibrary: ChargebeeGlobal;

    try {
      chargebeeLibrary = await loadChargebeeScript();
    } catch {
      toast.error("Payment system unavailable. Please try again later.");
      window.location.href = "/dashboard/subscription";
      return;
    }

    chargebeeLibrary.tearDown();
    const instance = chargebeeLibrary.init({
      site: resolveChargebeeSite(),
    });

    instance.openCheckout({
      hostedPage: async () => {
        const result = await fetch("/profile/billing/changeCardHp");
        return result.json();
      },
      success: async (hostedPageId: string) => {
        await fetch("/profile/billing/acknowledgeHostedPage", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ pageId: hostedPageId }),
        });
        toast.success("Payment method updated successfully!");
        queryClient.invalidateQueries({ queryKey: ["initial-user"] });
        dismissBanner();
      },
      close: () => {
        // User closed modal without completing
      },
    });
  };

  return (
    <div className="sticky top-0 z-50 w-full bg-red-50 border-b-2 border-red-200 px-6 py-4">
      <div className="flex items-center justify-between gap-4">
        {/* Icon + Message */}
        <div className="flex items-center gap-3 flex-1">
          <i className="fa-regular fa-circle-exclamation text-red-600 text-xl"></i>
          <div className="text-sm text-red-900">
            {isAdmin ? (
              <>
                <span className="font-semibold">
                  {dueInvoicesCount}{" "}
                  {dueInvoicesCount === 1 ? "invoice" : "invoices"} overdue
                </span>
                {totalDues && currencyCode && (
                  <span className="ml-2">
                    ({formatCurrencyFromCents(totalDues, currencyCode)} total)
                  </span>
                )}
                {dueSince && (
                  <span className="ml-2">
                    since {format(new Date(dueSince * 1000), "MMM d, yyyy")}
                  </span>
                )}
                <span className="block mt-1 text-neutral-700">
                  Your account may be cancelled and posts won't publish until
                  payment is received.
                </span>
              </>
            ) : (
              <>
                <span className="font-semibold">
                  Outstanding payment required
                </span>
                <span className="block mt-1 text-neutral-700">
                  There are overdue invoices on this account. Please contact
                  your account admin
                  {adminEmail && (
                    <span className="font-semibold"> ({adminEmail})</span>
                  )}{" "}
                  to update payment information.
                </span>
              </>
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {isAdmin && (
            <Button
              variant="red-primary"
              size="md"
              onClick={handleUpdatePayment}
            >
              <i className="fa-regular fa-credit-card"></i>
              Update Payment Method
            </Button>
          )}

          {/* Dismiss button */}
          <button
            onClick={dismissBanner}
            className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-100 transition-colors"
            aria-label="Dismiss banner"
          >
            <i className="fa-regular fa-times text-lg"></i>
          </button>
        </div>
      </div>
    </div>
  );
}
